import {
  createStyles,
  makeStyles,
  Theme,
  useTheme
} from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';
import { ChartOptions } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { SumProjectProgress } from '../pages/Project/projectSlice';
import { formatCountDocument } from './ChartCountDocument';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    chart: {
      height: 400,
      [theme.breakpoints.down('sm')]: {
        height: 300
      },
      [theme.breakpoints.down('xs')]: {
        height: 200
      }
    }
  })
);

interface ChartSumProjectProgressProps {
  renderSumProjectProgress: SumProjectProgress;
  loading?: boolean;
}

export default function ChartSumProjectProgress({
  renderSumProjectProgress,
  loading = false
}: ChartSumProjectProgressProps) {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();

  const startDate = renderSumProjectProgress.data[0]
    ? moment(renderSumProjectProgress.data[0].month).add(-1, 'months')
    : moment().startOf('month').add(-1, 'months');
  const endDate = renderSumProjectProgress.data[
    renderSumProjectProgress.data.length - 1
  ]
    ? moment(
        renderSumProjectProgress.data[renderSumProjectProgress.data.length - 1]
          .month
      ).add(1, 'months')
    : moment().startOf('month').add(1, 'months');

  const sumProjectProgressesData = formatCountDocument({
    startDate,
    endDate,
    documentRevisionCounts: renderSumProjectProgress.data
  });

  const totalManHours = renderSumProjectProgress.sumTotalManHours;

  let accumulateProjectProgresses: {
    month: string;
    accumulatePercentPlan: number;
    accumulatePercentActual: number;
  }[] = [];

  sumProjectProgressesData.forEach((sumProjectProgress, index) => {
    const percentPlan = (sumProjectProgress.plan / totalManHours) * 100;

    const percentActual = (sumProjectProgress.actual / totalManHours) * 100;
    if (index === 0) {
      accumulateProjectProgresses.push({
        month: sumProjectProgress.month,
        accumulatePercentPlan: percentPlan,
        accumulatePercentActual: percentActual
      });
    } else {
      const lastPercentPlan =
        accumulateProjectProgresses[accumulateProjectProgresses.length - 1]
          .accumulatePercentPlan;

      const lastPercentActual =
        accumulateProjectProgresses[accumulateProjectProgresses.length - 1]
          .accumulatePercentActual;

      accumulateProjectProgresses.push({
        month: sumProjectProgress.month,
        accumulatePercentPlan: percentPlan + lastPercentPlan,
        accumulatePercentActual: percentActual + lastPercentActual
      });
    }
  });

  const options: ChartOptions<'line'> = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        display: true,
        position: 'bottom'
      }
    },
    scales: {
      xAxes: {
        display: true
      },
      y: {
        display: true,
        position: 'left',
        min: 0,
        grid: {
          display: false
        }
      }
    }
  };

  const data = {
    labels: accumulateProjectProgresses.map(
      (accumulateProjectProgress) => accumulateProjectProgress.month
    ),
    datasets: [
      {
        type: 'line' as const,
        label: t('chartOverallProgress.accumPlan') as string,
        backgroundColor: theme.palette.background.paper,
        borderColor: '#cccccc',
        borderWidth: 2,
        fill: false,
        data: accumulateProjectProgresses.map(
          (accumulateProjectProgress) =>
            accumulateProjectProgress.accumulatePercentPlan
        ),
        yAxisID: 'y'
      },
      {
        type: 'line' as const,
        label: t('chartOverallProgress.accumActual') as string,
        backgroundColor: theme.palette.background.paper,
        borderColor: '#1877f2',
        borderWidth: 2,
        fill: false,
        data: accumulateProjectProgresses.map(
          (accumulateProjectProgress) =>
            accumulateProjectProgress.accumulatePercentActual
        ),
        yAxisID: 'y'
      }
    ]
  };

  return (
    <div className={classes.chart}>
      {loading ? (
        <Skeleton
          animation="wave"
          variant="rect"
          height={400}
          style={{ borderRadius: 8 }}
        />
      ) : (
        <Chart type="line" data={data} options={options} />
      )}
    </div>
  );
}
